import styled from '@emotion/styled';
import { Delete } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Cell } from 'react-table';
import { deleteTeamMember, ITeamMembersResponse, IInviteStatus } from 'wisestep-api';
import { Notification, WsColors, WsDataTable } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { TeamAccessAction, useTeamAccessAuthz } from '../../../hooks/useTeamAccessAuthz';

interface IStatusTypographyProps {
  cell: Cell;
}

const bgcolorStatusMap = new Map();

bgcolorStatusMap.set(IInviteStatus.INVITED, WsColors.Yellow100);
bgcolorStatusMap.set(IInviteStatus.ACTIVATED, WsColors.Green100);
bgcolorStatusMap.set(IInviteStatus.DEACTIVATED, WsColors.Red100);

const colorStatusMap = new Map();

colorStatusMap.set(IInviteStatus.INVITED, WsColors.Yellow500);
colorStatusMap.set(IInviteStatus.ACTIVATED, WsColors.Green500);
colorStatusMap.set(IInviteStatus.DEACTIVATED, WsColors.Red500);

export const StatusTypography = styled(Typography)(({ cell }: IStatusTypographyProps) => ({
  backgroundColor: bgcolorStatusMap.get(cell.value),
  color: colorStatusMap.get(cell.value),
  padding: 4,
  width: 'fit-content',
  borderRadius: 4,
}));

export interface ITeamMembersTableProps {
  teamMembers: ITeamMembersResponse[] | null;
  onDeleteMember: () => void;
}

const TeamMembersTable = ({ teamMembers, onDeleteMember }: ITeamMembersTableProps) => {
  const [deleteMemberSuccess, setDeleteMemberSuccess] = useState<boolean>(false);

  const admin = useWorkspaceContext();

  const { access: teamAccessControl } = useTeamAccessAuthz([TeamAccessAction.MANAGE_TEAMS_ACCESS]);

  const manageAccess = admin.owns || teamAccessControl[0];

  const handleDeleteTeamMember = useCallback(
    async (email: string, teamId: string) => {
      await deleteTeamMember(email, teamId);
      setDeleteMemberSuccess(true);
      onDeleteMember();
    },
    [onDeleteMember]
  );
  const columns = useMemo(
    () => [
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Invite Status',
        accessor: 'status',
        Cell: (cell: Cell) => <StatusTypography cell={cell}>{cell.value}</StatusTypography>,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: (cell: Cell) => (
          <IconButton
            onClick={() =>
              teamMembers &&
              handleDeleteTeamMember(
                teamMembers[cell.row.index].memberEmail,
                teamMembers[cell.row.index].teamId
              )
            }
            disabled={!manageAccess || ownerEmail(cell.row.index)}
          >
            <Delete />
          </IconButton>
        ),
      },
    ],
    [manageAccess, handleDeleteTeamMember, teamMembers]
  );

  const ownerEmail = (index) => {
    return admin.userId === teamMembers?.[index]?.memberEmail;
  };

  const data = useMemo(
    () =>
      teamMembers &&
      teamMembers.map((member) => ({
        email: member.memberEmail,
        status: IInviteStatus.ACTIVATED,
      })),
    [teamMembers]
  );
  return (
    <>
      <WsDataTable columns={columns} data={data || []} boldItems={['email']} />
      {deleteMemberSuccess && (
        <Notification
          vertical="top"
          horizontal="center"
          autoHideDuration={3000}
          notificationObject={{
            message: 'Member removed from team successfully!',
            type: 'success',
          }}
          notify={true}
        />
      )}
    </>
  );
};

export default TeamMembersTable;
